/**
 * CombineSegmentsModal (A1 multi-segment combine).
 *
 * Lets a user build a reusable *static snapshot* list from the combined audience of several segments:
 * the UNION of the "Include" segments, minus the UNION of the optional "Exclude" segments, deduped by
 * contact. Dynamic and static source segments are both allowed — the output is always a frozen static
 * list, so it is safe to attach to voucher flights.
 *
 * Flow: pick includes (+ optional excludes) → best-effort debounced preview count → name → Create.
 * create_combined_list returns a list in build_status='building'; the async drainer (cron) materializes
 * the membership. We poll until 'ready' (waitForListReady) and only then hand the finished list back to
 * the caller, because the DB trigger rejects attaching a non-ready combined list to a scheduling flight.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import Modal from "../common/Modal";
import {
  getLists,
  countCombinedSegmentAudience,
  createCombinedList,
  waitForListReady,
  type CombinedAudienceCount,
} from "../../services/lists.service";
import type { List } from "../../types/list.types";
import type { UUID } from "../../types/common.types";

interface CombineSegmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the fully-materialized (build_status='ready') combined list. */
  onCreated: (list: List) => void;
}

/** A segment is eligible as a source if it is a normal list (not itself a still-building combined list). */
function isSelectable(l: List): boolean {
  return !l.build_status || l.build_status === "ready";
}

export default function CombineSegmentsModal({
  isOpen,
  onClose,
  onCreated,
}: CombineSegmentsModalProps) {
  const [lists, setLists] = useState<List[]>([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [listsError, setListsError] = useState<string | null>(null);

  const [includeIds, setIncludeIds] = useState<UUID[]>([]);
  const [excludeIds, setExcludeIds] = useState<UUID[]>([]);
  const [search, setSearch] = useState("");
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);

  const [preview, setPreview] = useState<CombinedAudienceCount | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [buildStatus, setBuildStatus] = useState<
    "idle" | "building" | "failed"
  >("idle");
  const [createError, setCreateError] = useState<string | null>(null);

  // Reset all state each time the modal opens.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setIncludeIds([]);
    setExcludeIds([]);
    setSearch("");
    setName("");
    setNameTouched(false);
    setPreview(null);
    setPreviewError(null);
    setCreateError(null);
    setBuildStatus("idle");
    setCreating(false);

    setLoadingLists(true);
    setListsError(null);
    getLists()
      .then((rows) => setLists(rows.filter(isSelectable)))
      .catch((e) => setListsError(e?.message ?? "Failed to load segments"))
      .finally(() => setLoadingLists(false));
  }, [isOpen]);

  const listById = useMemo(() => {
    const m = new Map<UUID, List>();
    for (const l of lists) {
      m.set(l.id, l);
    }
    return m;
  }, [lists]);

  // Suggested default name from the included segments (user can override).
  useEffect(() => {
    if (nameTouched) {
      return;
    }
    const names = includeIds
      .map((id) => listById.get(id)?.name)
      .filter(Boolean) as string[];
    if (names.length === 0) {
      setName("");
    } else if (names.length <= 2) {
      setName(`Combined: ${names.join(" + ")}`);
    } else {
      setName(
        `Combined: ${names.slice(0, 2).join(" + ")} +${names.length - 2}`,
      );
    }
  }, [includeIds, listById, nameTouched]);

  // Debounced best-effort preview count. Recompute whenever the include/exclude selection changes.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (includeIds.length === 0) {
      setPreview(null);
      setPreviewError(null);
      setPreviewing(false);
      return;
    }
    setPreviewing(true);
    setPreviewError(null);
    const inc = [...includeIds];
    const exc = [...excludeIds];
    debounceRef.current = setTimeout(() => {
      countCombinedSegmentAudience(inc, exc)
        .then((c) => setPreview(c))
        .catch((e) => {
          setPreview(null);
          setPreviewError(e?.message ?? "Could not estimate audience size");
        })
        .finally(() => setPreviewing(false));
    }, 600);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [includeIds, excludeIds]);

  const toggle = (id: UUID, which: "include" | "exclude") => {
    if (which === "include") {
      setIncludeIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
      // A segment can't be both included and excluded.
      setExcludeIds((prev) => prev.filter((x) => x !== id));
    } else {
      setExcludeIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
      setIncludeIds((prev) => prev.filter((x) => x !== id));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return lists;
    }
    return lists.filter((l) => l.name.toLowerCase().includes(q));
  }, [lists, search]);

  const canCreate =
    includeIds.length >= 1 && name.trim().length > 0 && !creating;

  const handleCreate = async () => {
    if (!canCreate) {
      return;
    }
    setCreating(true);
    setCreateError(null);
    setBuildStatus("building");
    try {
      const id = await createCombinedList(includeIds, excludeIds, name.trim());
      const ready = await waitForListReady(id);
      if (ready.build_status === "failed") {
        setBuildStatus("failed");
        setCreateError(
          (ready.metadata?.build_error as string) ??
            "The combined list failed to build. Please try again.",
        );
        setCreating(false);
        return;
      }
      onCreated(ready);
      onClose();
    } catch (e) {
      setBuildStatus("failed");
      setCreateError(
        (e as Error)?.message ?? "Failed to create the combined list",
      );
      setCreating(false);
    }
  };

  const renderRow = (l: List) => {
    const included = includeIds.includes(l.id);
    const excluded = excludeIds.includes(l.id);
    return (
      <div
        key={l.id}
        className="flex items-center justify-between gap-2 border-b border-gray-100 py-2 last:border-0"
      >
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-gray-800">
            {l.name}
          </div>
          <div className="text-xs text-gray-400">
            {l.is_dynamic ? "Dynamic" : "Static"}
            {typeof l.contact_count === "number"
              ? ` · ${l.contact_count.toLocaleString()} contacts`
              : ""}
          </div>
        </div>
        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            onClick={() => toggle(l.id, "include")}
            className={`rounded px-2 py-1 text-xs font-medium ${
              included
                ? "bg-green-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {included ? "✓ Include" : "Include"}
          </button>
          <button
            type="button"
            onClick={() => toggle(l.id, "exclude")}
            className={`rounded px-2 py-1 text-xs font-medium ${
              excluded
                ? "bg-red-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {excluded ? "✓ Exclude" : "Exclude"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={creating ? () => {} : onClose}
      title="Combine segments into a list"
      size="lg"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          Build a reusable static list from several segments: everyone in the{" "}
          <span className="font-medium text-green-700">included</span> segments,
          minus anyone in the{" "}
          <span className="font-medium text-red-700">excluded</span> segments
          (deduped by contact). The result is a frozen snapshot of today's
          membership — combine again later to make a fresh one.
        </p>

        {/* Selection summary + preview count */}
        <div className="rounded-md bg-gray-50 p-3 text-sm">
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <span>
              <span className="font-medium">{includeIds.length}</span> included
            </span>
            <span>
              <span className="font-medium">{excludeIds.length}</span> excluded
            </span>
            <span className="text-gray-600">
              {previewing ? (
                "Estimating audience…"
              ) : preview ? (
                <>
                  ≈{" "}
                  <span className="font-semibold text-gray-900">
                    {preview.deduped_contacts.toLocaleString()}
                  </span>{" "}
                  contacts
                  {(() => {
                    // total_before_dedup counts every include row (UNION ALL); excluded_count is removed by
                    // the exclude segments; the remainder is true cross-list duplicates.
                    const dupes = Math.max(
                      0,
                      preview.total_before_dedup -
                        preview.deduped_contacts -
                        preview.excluded_count,
                    );
                    const parts: string[] = [];
                    if (dupes > 0) {
                      parts.push(
                        `${dupes.toLocaleString()} duplicates removed`,
                      );
                    }
                    if (preview.excluded_count > 0) {
                      parts.push(
                        `${preview.excluded_count.toLocaleString()} excluded`,
                      );
                    }
                    return parts.length ? (
                      <span className="text-gray-400">
                        {" "}
                        ({parts.join(" · ")})
                      </span>
                    ) : null;
                  })()}
                </>
              ) : includeIds.length === 0 ? (
                "Select at least one segment to include"
              ) : previewError ? (
                <span className="text-amber-600">
                  Estimate unavailable (the list will still build correctly)
                </span>
              ) : (
                ""
              )}
            </span>
          </div>
        </div>

        {/* Search + list picker */}
        <div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search segments…"
            className="mb-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            disabled={creating}
          />
          <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200 px-3">
            {loadingLists ? (
              <p className="py-6 text-center text-sm text-gray-400">
                Loading segments…
              </p>
            ) : listsError ? (
              <p className="py-6 text-center text-sm text-red-600">
                {listsError}
              </p>
            ) : filtered.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-400">
                No segments match “{search}”.
              </p>
            ) : (
              filtered.map(renderRow)
            )}
          </div>
        </div>

        {/* Name */}
        <div>
          <label
            htmlFor="combined-name"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            List name <span className="text-red-500">*</span>
          </label>
          <input
            id="combined-name"
            type="text"
            value={name}
            onChange={(e) => {
              setNameTouched(true);
              setName(e.target.value);
            }}
            placeholder="e.g. Newsletter A + B subscribers"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            disabled={creating}
          />
        </div>

        {createError && <p className="text-sm text-red-600">{createError}</p>}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3">
          <button
            type="button"
            onClick={onClose}
            disabled={creating}
            className="rounded-md px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={!canCreate}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {buildStatus === "building" && creating
              ? "Building list…"
              : "Create combined list"}
          </button>
        </div>
        {creating && (
          <p className="text-right text-xs text-gray-400">
            Materializing the audience — this can take up to a minute for large
            segments.
          </p>
        )}
      </div>
    </Modal>
  );
}
